import React from "react";
import AddressDisplay from "../../../../../components/ui/display/AddressDisplay";
import ContentCard from "../../../../../components/ui/cards/ContentCard";
import { usaStates, businessFilingStatuses } from "../../../../../components/ui/forms/selectOptionData";

// Helper function to convert state code to full state name
const getStateName = (stateCode) => {
  if (!stateCode) return stateCode;
  const stateCodeStr = typeof stateCode === 'string' ? stateCode : String(stateCode);
  const state = usaStates().find((s) => s.value === stateCodeStr.toLowerCase());
  return state ? state.label : stateCodeStr;
};

// Helper to get business filing status label
const getBusinessFilingStatusLabel = (value) => {
  const status = businessFilingStatuses().find((s) => s.value === value);
  return status ? status.label : value;
};

// Helper function to format state registration array
const formatStateRegistration = (stateRegistration) => {
  if (!stateRegistration) return null;
  if (Array.isArray(stateRegistration)) {
    return stateRegistration.map(s => getStateName(s)).join(", ");
  }
  return getStateName(stateRegistration);
};

// Helper component to render a row with label and value
const InfoRow = ({ label, value }) => {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div className="info-row">
      <span className="info-row-label">{label}:</span>
      <span className="info-row-value">{value}</span>
    </div>
  );
};

const BTFBackgroundSection = ({ background, situation }) => {
  if (!background && !situation) return null;

  const hasBusinessInfo = background?.businessName || background?.yearEstablished || 
                          background?.ownersNumber || background?.stateRegistration ||
                          background?.distributionDividendsIssued !== undefined ||
                          background?.ownershipChanges !== undefined;
  const hasBusinessAddress = background?.businessAddress;
  const hasFilingStatus = situation?.taxYears || situation?.filingStatus || 
                          situation?.businessClosedMerged !== undefined;

  if (!hasBusinessInfo && !hasBusinessAddress && !hasFilingStatus) return null;

  // Format filing status data
  const formattedTaxYears = Array.isArray(situation?.taxYears) 
    ? situation.taxYears.join(", ") 
    : situation?.taxYears || null;
  const formattedFilingStatus = situation?.filingStatus 
    ? getBusinessFilingStatusLabel(situation.filingStatus) 
    : null;

  return (
    <>
      {/* Business Filing Basics Card */}
      {hasFilingStatus && (
        <ContentCard style={{ borderRadius: "0" }}>
          <h4 className="content-card-title">Business Filing Basics</h4>
          <div className="content-card-rows">
            <InfoRow label="Filing Year(s)" value={formattedTaxYears} />
            <InfoRow label="Filing Status" value={formattedFilingStatus} />
            {situation?.businessClosedMerged !== undefined && situation?.businessClosedMerged !== null && (
              <InfoRow 
                label="Business Closed/Merged" 
                value={situation.businessClosedMerged?.value === true ? "Yes" : "No"} 
              />
            )}
          </div>
        </ContentCard>
      )}

      {/* Business Information Card */}
      {hasBusinessInfo && (
        <ContentCard style={{ borderRadius: "0" }}>
          <h4 className="content-card-title">Business Information</h4>
          <div className="content-card-rows">
            <InfoRow label="Business Name" value={background?.businessName} />
            <InfoRow label="Year Established" value={background?.yearEstablished} />
            <InfoRow label="Number of Owners" value={background?.ownersNumber} />
            <InfoRow label="State Registration" value={formatStateRegistration(background?.stateRegistration)} />
            {background?.distributionDividendsIssued !== undefined && background?.distributionDividendsIssued !== null && (
              <InfoRow 
                label="Distribution/Dividends Issued" 
                value={background.distributionDividendsIssued?.value === true ? "Yes" : "No"} 
              />
            )}
            {background?.ownershipChanges !== undefined && background?.ownershipChanges !== null && (
              <InfoRow 
                label="Ownership Changes" 
                value={background.ownershipChanges?.value === true ? "Yes" : "No"} 
              />
            )}
          </div>
        </ContentCard>
      )}

      {/* Business Address Card */}
      {hasBusinessAddress && (
        <ContentCard style={{ borderRadius: "0" }}>
          <h4 className="content-card-title">Business Address</h4>
          <AddressDisplay
            address={{
              address: background.businessAddress.address,
              city: background.businessAddress.city,
              stateAddress: getStateName(background.businessAddress.stateAddress),
              zipCode: background.businessAddress.zipCode,
              suiteAddress: background.businessAddress.suiteAddress,
            }}
            showDates={false}
          />
        </ContentCard>
      )}
    </>
  );
};

export default BTFBackgroundSection;

